import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQrcode, faDownload, faSync, faPrint } from '@fortawesome/free-solid-svg-icons';
import { jsPDF } from 'jspdf';
import { toast } from 'react-toastify';
import { generateRoomQr } from '../Controller/BillPostingSettingsController';
import ModalGlobal from '../../../../GlobalModules/Utility/Components/ModalGlobal';
import ErrorBoundary from '../../../../GlobalModules/Utility/Components/ErrorBoundary';
import Utility from '../../../../GlobalModules/Utility/Utility';

const GenerateRoomQrModal = ({ show, onHide, storeId, t = (key) => key }) => {
  const [loading, setLoading] = useState(false);
  const [printingPdf, setPrintingPdf] = useState(false);
  const [roomQrData, setRoomQrData] = useState([]);
  const [roomQrError, setRoomQrError] = useState('');
  const [zoominzoomoutflag, setZoominzoomoutflag] = useState('');

  useEffect(() => {
    if (show && roomQrData.length === 0) {
      fetchRoomQrs();
    }
  }, [show]);

  const fetchRoomQrs = async () => {
    setLoading(true);
    setRoomQrError('');
    try {
      const currentStoreId = storeId || localStorage.getItem('store_id');
      const response = await generateRoomQr(currentStoreId);
      if (response.success) {
        setRoomQrData(response.data || []);
      } else {
        setRoomQrError(response.message || 'Failed to generate Room QR codes');
      }
    } catch (err) {
      setRoomQrError(err.message || 'Error occurred while generating Room QR codes');
    } finally {
      setLoading(false);
    }
  };

  const zoominFunction = () => {
    setZoominzoomoutflag('zoominzoomoutclassDialog');
  };

  const zoomoutFunction = () => {
    setZoominzoomoutflag('');
  };

  const handleClose = () => {
    setZoominzoomoutflag('');
    if (onHide) {
      onHide();
    }
  };

  const handleDownloadQr = async (filePath, roomNumber) => {
    try {
      if (!filePath) return;
      const filename = `Room_${roomNumber}_QR.svg`;

      const response = await fetch(filePath);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading QR code via blob, falling back to direct link:', error);
      const link = document.createElement('a');
      link.href = filePath;
      link.download = `Room_${roomNumber}_QR.svg`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const convertSvgToPng = (svgDataUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width || 800;
        canvas.height = img.height || 600;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = (err) => reject(err);
      img.src = svgDataUrl;
    });
  };

  const handlePrintAllPdf = async () => {
    if (!roomQrData || roomQrData.length === 0) return;
    setPrintingPdf(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4'); // A4: 210mm x 297mm
      const cols = 3;
      const rows = 4; // 8 room cards per A4 page
      const cardsPerPage = cols * rows;

      const marginLeft = 20;
      const marginTop = 15;
      const cardWidth = 50;
      const cardHeight = 60; // Matches SVG ratio (900x520)
      const gapX = 8;
      const gapY = 12;

      for (let i = 0; i < roomQrData.length; i++) {
        const room = roomQrData[i];
        const indexOnPage = i % cardsPerPage;

        if (i > 0 && indexOnPage === 0) {
          pdf.addPage();
        }

        const colIdx = indexOnPage % cols;
        const rowIdx = Math.floor(indexOnPage / cols);

        const x = marginLeft + colIdx * (cardWidth + gapX);
        const y = marginTop + rowIdx * (cardHeight + gapY);

        // Dashed crop / cutting guide box around card
        pdf.setDrawColor(160, 160, 160);
        pdf.setLineWidth(0.3);
        pdf.setLineDashPattern([2, 2], 0);
        pdf.rect(x - 1, y - 1, cardWidth + 2, cardHeight + 2);

        // Cut line marker
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor(120, 120, 120);
        pdf.text('✂ Cut line', x + cardWidth - 1, y - 2, { align: 'right' });

        // Embed QR code PNG directly filling the compact card box
        if (room.qr_code_data_url) {
          try {
            const pngData = await convertSvgToPng(room.qr_code_data_url);
            pdf.addImage(pngData, 'PNG', x, y, cardWidth, cardHeight);
          } catch (err) {
            console.error('Error rendering SVG in PDF for room', room.room_number, err);
          }
        }
      }

      pdf.save(`Room_QR_Codes_All_${Date.now()}.pdf`);
      Utility.toastNotifications(t('Room QR codes PDF generated and downloaded!'), 'Success', 'success');
    } catch (err) {
      console.error('Error generating PDF:', err);
      Utility.toastNotifications(t('Failed to generate Room QR PDF'), 'Error', 'error');
    } finally {
      setPrintingPdf(false);
    }
  };

  return (
    <ErrorBoundary title="ModalGlobal Error">
      <ModalGlobal
        show={show}
        onHide={handleClose}
        className={` ${zoominzoomoutflag + ' ' + 'modalcustomizeDialog mondimension usersModalDialog'}`}
        bodyClassName="p-3"
        headerclassName="close_btn_icon"
        title={t('Generated Room QR Codes')}
        closeButton={true}
        footer={false}
        zoominButton={zoominzoomoutflag !== '' ? false : true}
        zoomoutButton={zoominzoomoutflag !== '' ? true : false}
        zoomin={zoominFunction}
        zoomout={zoomoutFunction}
        body={
          <>
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-3 text-muted">{t('Generating Room QR codes, please wait...')}</p>
              </div>
            ) : roomQrError ? (
              <Alert variant="danger" className="d-flex justify-content-between align-items-center mb-0">
                <div>{roomQrError}</div>
                <Button size="sm" variant="outline-danger" onClick={fetchRoomQrs}>
                  <FontAwesomeIcon icon={faSync} className="me-1" />
                  {t('Retry')}
                </Button>
              </Alert>
            ) : roomQrData.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted">{t('No room QR codes available.')}</p>
                <Button variant="primary" size="sm" onClick={fetchRoomQrs}>
                  <FontAwesomeIcon icon={faQrcode} className="me-1" />
                  {t('Generate Room QRs')}
                </Button>
              </div>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3 px-1">
                  <span className="fw-bold text-secondary" style={{ fontSize: '0.9rem' }}>
                    {t('Total Rooms')}: {roomQrData.length}
                  </span>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant="success"
                      onClick={handlePrintAllPdf}
                      disabled={printingPdf || loading}
                      className="d-flex align-items-center gap-1"
                    >
                      <FontAwesomeIcon icon={faPrint} />
                      {printingPdf ? t('Generating PDF...') : t('Print All (PDF)')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={fetchRoomQrs}
                      disabled={loading || printingPdf}
                      className="d-flex align-items-center gap-1"
                    >
                      <FontAwesomeIcon icon={faSync} />
                      {t('Regenerate All')}
                    </Button>
                  </div>
                </div>
                <Row className="g-2" style={{ maxHeight: '68vh', overflowY: 'auto' }}>
                  {roomQrData.map((room, idx) => (
                    <Col key={room.room_number || idx} xs={12} sm={6} md={4} lg={4}>
                      <Card className="h-100 text-center shadow-sm border rounded">
                        <Card.Body className="p-2 d-flex flex-column align-items-center justify-content-between">
                          {room.qr_code_data_url ? (
                            <img
                              src={room.qr_code_data_url}
                              alt={`Room ${room.room_number} QR`}
                              className="img-fluid mb-2"
                              style={{ width: '100%', height: 'auto', maxHeight: '200px', objectFit: 'contain' }}
                            />
                          ) : (
                            <div className="text-muted my-2 small">{t('No QR Image')}</div>
                          )}
                          {room.qr_code_file_path && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="w-100 mt-1 py-1"
                              style={{ fontSize: '0.8rem' }}
                              onClick={() => handleDownloadQr(room.qr_code_file_path, room.room_number)}
                            >
                              <FontAwesomeIcon icon={faDownload} className="me-1" />
                              {t('Download QR')}
                            </Button>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </>
            )}
          </>
        }
      />
    </ErrorBoundary>
  );
};

export default GenerateRoomQrModal;
